/**
 * Entregar y cobrar. El ultimo paso, y el unico que mueve plata en serio.
 *
 * LO QUE SE VE ANTES DE APRETAR
 * -----------------------------
 * Cuanto sale, cuanto ya se cobro a cuenta, cuanto falta. Y abajo, con que
 * paga. «A cuenta» y no «seña»: lo cobrado incluye el diagnostico, y en un
 * equipo que vuelve sin arreglo decia «Seña ya cobrada» sobre una reparacion
 * que no se hizo.
 * Si queda diferencia y hay cliente cargado, va a su cuenta corriente y el
 * dialogo lo dice. Si no hay cliente, no se puede entregar sin cobrar entero:
 * es la regla de `entregar_orden`, y se muestra aca en vez de descubrirla en
 * un error rojo.
 *
 * "Volvio sin arreglo" cambia la cuenta: se factura solo el diagnostico, que
 * puede estar pago o pendiente (`cobroAlVolver()`).
 *
 * Lo cobrado de mas se trata como en la venta (0145, 0155): con o sin cliente
 * es vuelto, siempre en pesos del cajon de Efectivo en pesos, o propina si el
 * cliente lo deja. Y el saldo a favor del cliente se ofrece, nunca se aplica
 * solo.
 */
import { useEffect, useMemo, useState } from "react";
import { KeyRound } from "lucide-react";
import { convertir, formatear, redondear, type Moneda } from "@tora/shared";

import { EditorPagos, nuevoPago } from "@/components/cobro/EditorPagos";
import { nombreCaja, type CuentaFinanciera } from "@/components/cobro/logica";
import { Importe } from "@/components/money";
import { InputImporte } from "@/components/money/input-importe";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Segmented } from "@/components/ui/segmented";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import type { EntregaOt } from "@/lib/tipos";

import {
  armarEntrega,
  cobroAlVolver,
  entregaInicial,
  evaluarEntrega,
  llevaGarantia,
  nombreCliente,
  nombreOrden,
  type FormularioEntrega,
  type OrdenTaller,
} from "./logica";

/**
 * Las tres salidas del equipo. `Segmented` pide valores string, asi que el
 * `null` del formulario ("se entrega reparado") viaja como "REPARADO".
 */
type Cierre = "REPARADO" | "SIN_ARREGLO" | "RECHAZADO";

const CIERRES: ReadonlyArray<{ valor: Cierre; etiqueta: string }> = [
  { valor: "REPARADO", etiqueta: "Sí, se entrega reparado" },
  { valor: "SIN_ARREGLO", etiqueta: "No tiene arreglo" },
  { valor: "RECHAZADO", etiqueta: "El cliente no lo aprobó" },
];

export interface DialogEntregarProps {
  orden: OrdenTaller | null;
  cuentas: CuentaFinanciera[];
  cotizacion: number;
  guardando?: boolean;
  /**
   * El saldo a favor que el cliente puede usar, en la moneda de su cuenta
   * (`v_saldo_a_favor_clientes`, 0121_8), y esa moneda. Se ofrece como medio
   * de pago, igual que en la venta.
   */
  saldoAFavor?: number | null;
  clienteAnclaje?: Moneda | null;
  onCerrar: () => void;
  onConfirmar: (entrega: EntregaOt) => void;
}

export function DialogEntregar({
  orden,
  cuentas,
  cotizacion,
  guardando = false,
  saldoAFavor = null,
  clienteAnclaje = null,
  onCerrar,
  onConfirmar,
}: DialogEntregarProps): JSX.Element {
  const [form, setForm] = useState<FormularioEntrega>({
    pagos: [],
    cierre: null,
    cobrarDiagnostico: true,
    precioVenta: null,
  });

  // Al abrir se propone un pago por lo que falta: es lo que pasa casi siempre.
  useEffect(() => {
    if (!orden) return;
    const base = entregaInicial(orden);
    const ev = evaluarEntrega(orden, base, cuentas, cotizacion);
    const p = ev.resta > 0 ? nuevoPago(cuentas, [], ev.resta, ev.moneda, cotizacion) : null;
    setForm({ ...base, pagos: p ? [p] : [] });
  }, [orden, cuentas, cotizacion]);

  const ev = useMemo(
    () => (orden ? evaluarEntrega(orden, form, cuentas, cotizacion) : null),
    [orden, form, cuentas, cotizacion],
  );

  if (!orden || !ev) {
    return <Dialog open={false} onOpenChange={() => onCerrar()} />;
  }

  const yaCerrada = orden.estado === "SIN_ARREGLO" || orden.estado === "RECHAZADO";
  const conCliente = orden.cliente_id !== null && orden.cliente_id !== undefined;

  /** El saldo a favor, pasado a la moneda de la entrega. Hasta lo que resta. */
  const saldoDisponible =
    conCliente && clienteAnclaje !== null && (saldoAFavor ?? 0) > 0.009
      ? convertir(saldoAFavor ?? 0, clienteAnclaje, ev.moneda, cotizacion)
      : 0;
  const topeSaldo = redondear(Math.max(Math.min(ev.resta, saldoDisponible), 0));
  const saldoUsado = redondear(Math.min(form.saldoAFavor ?? 0, topeSaldo));
  /** El cajon de donde sale el vuelto, con su nombre. */
  const cajaVuelto = ev.cajaVuelto
    ? (cuentas.find((c) => c.id === ev.cajaVuelto?.id) ?? null)
    : null;

  /**
   * Otro motivo de cierre (o el saldo a favor) cambia lo que falta: se vuelve a
   * proponer el pago, como al abrir. Antes el cambio dejaba la lista vacia, y
   * con cliente el dialogo decia en verde «Quedan US$ 56 en la cuenta
   * corriente» con la plata en la mano. Tocar el chip que ya estaba marcado no
   * cambia nada.
   */
  function reproponer(f: FormularioEntrega): FormularioEntrega {
    if (!orden) return f;
    const sinPagos = { ...f, pagos: [] };
    const nuevo = evaluarEntrega(orden, sinPagos, cuentas, cotizacion);
    const falta = redondear(nuevo.resta - (f.saldoAFavor ?? 0));
    const p = falta > 0.009 ? nuevoPago(cuentas, [], falta, nuevo.moneda, cotizacion) : null;
    return { ...sinPagos, pagos: p ? [p] : [] };
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="md">
        <DialogHeader>
          <DialogTitle>Entregar {orden.equipo}</DialogTitle>
          <DialogDescription>
            {nombreOrden(orden)} · {nombreCliente(orden)}. Se factura, se cobra y se cierra la
            orden, todo junto.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* ── Por que vuelve ──
              Dos motivos y no uno: "no tiene arreglo" es tecnico, "no lo
              aprobó" es comercial. Los dos devuelven el equipo y retienen el
              diagnóstico, pero solo el segundo mide lo que se pierde por
              precio. */}
          {!yaCerrada ? (
            <Card variant="muted" padding="compacto" className="space-y-2">
              <p className="text-sm font-medium">¿El equipo se reparó?</p>
              {/* `chips` y no `segmentado`: tres etiquetas de esta longitud no
                  entran en una sola fila a 375px, y las pildoras envuelven. */}
              <Segmented
                opciones={CIERRES}
                valor={form.cierre ?? "REPARADO"}
                onChange={(v) =>
                  setForm((f) =>
                    v === (f.cierre ?? "REPARADO")
                      ? f
                      : reproponer({
                          ...f,
                          cierre: v === "REPARADO" ? null : v,
                          // El saldo elegido se vuelve a elegir: el tope cambia.
                          saldoAFavor: undefined,
                        }),
                  )
                }
                etiqueta="¿El equipo se reparó?"
                apariencia="chips"
                tamano="sm"
              />
              {form.cierre !== null ? (
                <p className="text-xs text-muted-foreground">{cobroAlVolver(orden)}</p>
              ) : null}
            </Card>
          ) : (
            // El texto sale de la orden: «se devuelve sin cobrar» quedaba escrito
            // arriba de un «Falta cobrar US$ 20» cuando el diagnostico estaba
            // pendiente.
            <Aviso tono="neutral" compacto>
              {orden.estado === "RECHAZADO"
                ? "El cliente no aprobó el presupuesto."
                : "El equipo volvió sin arreglo."}{" "}
              {cobroAlVolver(orden)}
            </Aviso>
          )}

          {/* ── La cuenta ── */}
          <Card variant="muted" padding="compacto">
            <dl className="space-y-1.5 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">Precio</dt>
                <dd>
                  <Importe monto={ev.precio} moneda={ev.moneda} />
                </dd>
              </div>
              {/* Lo cobrado a cuenta entero, no solo lo que usa esta factura:
                  en un equipo sin arreglo con seña se mostraba «US$ 20» y los
                  otros US$ 52 no aparecian. Lo que sobra va en su linea. */}
              {ev.senas + ev.sobrante > 0 ? (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-muted-foreground">Cobrado a cuenta</dt>
                  <dd className="flex items-baseline gap-1">
                    <span aria-hidden="true">−</span>
                    <Importe monto={ev.senas + ev.sobrante} moneda={ev.moneda} />
                  </dd>
                </div>
              ) : null}
              {ev.sobrante > 0 ? (
                <div className="flex items-baseline justify-between gap-4">
                  {/* Sin cliente no hay cuenta corriente: decia «a favor del
                      cliente» arriba del aviso de que queda en la caja. */}
                  <dt className="text-muted-foreground">
                    {conCliente ? "Sobra, a favor del cliente" : "Sobra, se devuelve de vuelto"}
                  </dt>
                  <dd>
                    <Importe monto={ev.sobrante} moneda={ev.moneda} />
                  </dd>
                </div>
              ) : null}
              <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5">
                <dt className="font-medium">Falta cobrar</dt>
                <dd>
                  <Importe
                    monto={ev.resta}
                    moneda={ev.moneda}
                    tamano="lg"
                    className="font-semibold"
                  />
                </dd>
              </div>
            </dl>
          </Card>

          {/* ── Con que paga ── */}
          {ev.resta > 0 ? (
            <div className="space-y-2">
              <TituloSeccion nivel="etiqueta">¿Con qué paga?</TituloSeccion>
              {/* El saldo a favor del cliente, ofrecido y nunca aplicado solo,
                  como en la venta (0121_8). No figuraba: el taller, que es el
                  que genera esos saldos, no tenia forma de usarlos. */}
              {topeSaldo > 0.009 ? (
                <Card variant="muted" padding="compacto" className="space-y-2">
                  <Checkbox
                    checked={form.saldoAFavor !== undefined}
                    onChange={(e) =>
                      setForm((f) =>
                        reproponer({
                          ...f,
                          saldoAFavor: e.target.checked ? topeSaldo : undefined,
                        }),
                      )
                    }
                    label={
                      <>
                        Usar su saldo a favor:{" "}
                        <Importe monto={saldoDisponible} moneda={ev.moneda} tamano="sm" />
                      </>
                    }
                    ayuda="No sale de la caja ni se devuelve en plata: paga esta reparación."
                  />
                  {form.saldoAFavor !== undefined ? (
                    <InputImporte
                      label="¿Cuánto usa?"
                      valor={saldoUsado}
                      moneda={ev.moneda}
                      sinNegativos
                      ayuda={`Hasta ${formatear(topeSaldo, ev.moneda)}.`}
                      onValorChange={(v) => {
                        if (v !== null && v >= 0) {
                          setForm((f) => ({ ...f, saldoAFavor: Math.min(v, topeSaldo) }));
                        }
                      }}
                    />
                  ) : null}
                </Card>
              ) : null}
              <EditorPagos
                pagos={form.pagos}
                onChange={(pagos) => setForm((f) => ({ ...f, pagos }))}
                cuentas={cuentas}
                objetivo={redondear(ev.resta - saldoUsado)}
                monedaObjetivo={ev.moneda}
                cotizacion={cotizacion}
                permitirVacio={conCliente}
              />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {ev.precio === 0
                ? "No hay nada que cobrar."
                : "Ya está pago con lo cobrado a cuenta."}
            </p>
          )}

          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <KeyRound className="mt-px size-3 shrink-0" aria-hidden="true" />
            {/* Solo lo reparado lleva garantia (0131). Decia «se calcula la
                garantía» tambien al devolver un equipo sin arreglo. */}
            <span>
              {llevaGarantia(orden) && form.cierre === null
                ? "Al entregar se calcula la garantía y se borran la cuenta, la clave y el patrón del equipo."
                : "Sale sin garantía: solo lo reparado la lleva. Al entregar se borran la cuenta, la clave y el patrón del equipo."}
            </span>
          </p>

          {/* Lo que sobra no se devuelve solo: devolver o no es decision del
              local. Lo que no puede pasar es que el vendedor no se entere. */}
          {ev.sobrante > 0 ? (
            <Aviso tono="warning" compacto>
              Dejó <Importe monto={ev.sobrante} moneda={ev.moneda} tamano="sm" /> de más que esta
              factura no usa.{" "}
              {conCliente
                ? `Quedan a favor en la cuenta corriente de ${nombreCliente(orden)}: se usan en su próxima compra o reparación.`
                : "Se le devuelven de vuelto, en pesos, al entregar."}
            </Aviso>
          ) : null}

          {/* ── Que queda ── ultimo hijo del body: es lo que impide entregar. */}
          {/* Lo que queda a cuenta va en amarillo, no en verde: es plata que
              no entra hoy, y con el tilde verde se entregaba sin cobrar. */}
          {ev.puede && ev.aCuenta ? (
            <Aviso tono="warning">
              Quedan <Importe monto={ev.pendiente} moneda={ev.moneda} tamano="sm" /> en la cuenta
              corriente de {nombreCliente(orden)}.
            </Aviso>
          ) : ev.puede && ev.vuelto > 0 && cajaVuelto ? (
            <Aviso tono="success">
              {form.vueltoPropina ? (
                <>
                  Queda pago entero. Deja de propina{" "}
                  <Importe monto={ev.vuelto} moneda="ARS" tamano="sm" codigo={false} />: queda en{" "}
                  {nombreCaja(cajaVuelto)}.
                </>
              ) : (
                <>
                  Queda pago entero. Vuelto:{" "}
                  <Importe monto={ev.vuelto} moneda="ARS" tamano="sm" codigo={false} />, en pesos,
                  de {nombreCaja(cajaVuelto)}.
                </>
              )}
              <Checkbox
                className="mt-2"
                checked={form.vueltoPropina === true}
                onChange={(e) => setForm((f) => ({ ...f, vueltoPropina: e.target.checked }))}
                label="Lo deja de propina"
                ayuda="El vuelto no sale: queda en la caja."
              />
            </Aviso>
          ) : ev.puede ? (
            <Aviso tono="success">
              {ev.precio === 0 ? "Se entrega sin cobrar." : "Queda pago entero."}
            </Aviso>
          ) : (
            <Aviso tono="destructive">{ev.motivo}</Aviso>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            cargando={guardando}
            disabled={!ev.puede}
            onClick={() => onConfirmar(armarEntrega(orden, form))}
          >
            {ev.resta > 0 && ev.cobrado > 0 ? "Entregar y cobrar" : "Entregar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
